import { ReactNode, RefCallback, Ref, useCallback, MouseEvent } from 'react'
import { isEmpty, range } from 'ramda'
import cn from 'classnames'

import {
  Table,
  Td,
  TdProps,
  Th,
  Tr,
  createWidthCopyClassName,
} from 'app/common/ui-components/table'

import { CellDataType, RowDataType, TableData } from './types'
import { footerIndexBase, headerIndexBase } from './index-base'
import { mergePropsWithClassName } from './merge-props-with-class-name'

const columnIndexShift = 100

type Props<
  ColumnKey extends string,
  HeaderCellData,
  CellData extends CellDataType<ColumnKey>,
  RowData extends RowDataType<ColumnKey, CellData>,
  FooterCellData extends CellDataType<ColumnKey>,
  FooterRowData extends RowDataType<ColumnKey, FooterCellData>,
> = {
  className?: string
  data: TableData<ColumnKey, HeaderCellData, CellData, RowData, FooterCellData, FooterRowData>
  position: 'top' | 'middle' | 'bottom'
  renderHeaderCell: (cellData: { columnKey: ColumnKey; value?: string }) => ReactNode
  renderCell: (cellData: CellData) => ReactNode
  renderLoadingCell: (columnKey: ColumnKey) => ReactNode
  renderFooterCell: (cellData: FooterCellData) => ReactNode
  sticky?: boolean
  hasRightTable: boolean
  sameTableRowBaseClassName?: string
  sameTableColumnBaseClassName?: string
  reference?: Ref<HTMLTableElement>
  rowReferenceForCopyHeight?: RefCallback<HTMLTableRowElement>
  rowReferenceForCopyCellWidthsForHeaders?: RefCallback<HTMLTableRowElement>
  rowReferenceForCopyCellWidthsForFooters?: RefCallback<HTMLTableRowElement>
  loading?: boolean
  onMouseEnter?: (e: MouseEvent) => void
  onMouseLeave?: (e: MouseEvent) => void
  onCellMouseEnter?: (e: MouseEvent) => void
  onCellMouseLeave?: (e: MouseEvent) => void
  loadingRowsCount?: number
  cellsVerticalAlignTop?: boolean
  cellsVerticalAlignMiddle?: boolean
  cellsVerticalAlignBottom?: boolean
  headerCellsVerticalAlignTop?: boolean
  headerCellsVerticalAlignBottom?: boolean
  rowClassName?: string
  tdPropsByRowAndColumnKey: (params: {
    side: 'left' | 'right' | 'main'
    rowKey: string
    columnKey: string
  }) => Omit<TdProps, 'children' | 'className'>
}

export const MainTable = <
  ColumnKey extends string,
  HeaderCellData,
  CellData extends CellDataType<ColumnKey>,
  RowData extends RowDataType<ColumnKey, CellData>,
  FooterCellData extends CellDataType<ColumnKey>,
  FooterRowData extends RowDataType<ColumnKey, FooterCellData>,
>({
  className,
  data,
  position,
  renderHeaderCell,
  renderCell,
  renderLoadingCell,
  renderFooterCell,
  sticky,
  hasRightTable,
  sameTableRowBaseClassName = 'row_',
  sameTableColumnBaseClassName = 'column_',
  reference,
  rowReferenceForCopyHeight,
  rowReferenceForCopyCellWidthsForHeaders,
  rowReferenceForCopyCellWidthsForFooters,
  loading,
  onMouseEnter,
  onMouseLeave,
  onCellMouseEnter,
  onCellMouseLeave,
  loadingRowsCount = 10,
  cellsVerticalAlignTop,
  cellsVerticalAlignMiddle,
  cellsVerticalAlignBottom,
  headerCellsVerticalAlignTop,
  headerCellsVerticalAlignBottom,
  rowClassName,
  tdPropsByRowAndColumnKey,
}: Props<ColumnKey, HeaderCellData, CellData, RowData, FooterCellData, FooterRowData>) => {
  const {
    columnKeysMain,
    columnKeysMainHeaders,
    header,
    headerProps,
    data: rowsData,
    dataProps: rowsDataProps,
    total,
    totalProps,
  } = data

  const rowReferenceBothForHeaders: RefCallback<HTMLTableRowElement> = useCallback(
    (node) => {
      if (node && rowReferenceForCopyHeight) rowReferenceForCopyHeight(node)
      if (node && rowReferenceForCopyCellWidthsForHeaders)
        rowReferenceForCopyCellWidthsForHeaders(node)
    },
    [rowReferenceForCopyHeight, rowReferenceForCopyCellWidthsForHeaders],
  )

  const rowReferenceBothForFooters: RefCallback<HTMLTableRowElement> = useCallback(
    (node) => {
      if (node && rowReferenceForCopyHeight) rowReferenceForCopyHeight(node)
      if (node && rowReferenceForCopyCellWidthsForFooters)
        rowReferenceForCopyCellWidthsForFooters(node)
    },
    [rowReferenceForCopyHeight, rowReferenceForCopyCellWidthsForFooters],
  )

  const nothing = isEmpty(rowsData)

  const actualRows =
    nothing && loading
      ? range(1, loadingRowsCount).map((key) => ({
          rowKey: `${key}`,
          rowData: {} as Record<string, string>,
        }))
      : rowsData

  const cellsTdCommonProps = {
    verticalAlignTop: cellsVerticalAlignTop,
    verticalAlignMiddle: cellsVerticalAlignMiddle,
    verticalAlignBottom: cellsVerticalAlignBottom,
    onMouseEnter: onCellMouseEnter,
    onMouseLeave: onCellMouseLeave,
  }

  return (
    <Table className={className} reference={reference}>
      {/* в положении bottom не рендерится
       в положении middle скрывается */}
      {position !== 'bottom' && (
        <thead>
          {(columnKeysMainHeaders || [columnKeysMain]).map((columnKeys, index) => (
            <Tr
              key={index}
              header
              hidden={position === 'middle'}
              // ref для копирования высоты - только для положения top (когда видимо)
              // и ref для копирования ширины ячеек заголовков - для положений middle (from) и top (to)
              reference={
                position === 'top'
                  ? rowReferenceBothForHeaders
                  : position === 'middle'
                    ? rowReferenceForCopyCellWidthsForHeaders
                    : undefined
              }
              // index (он для копирования высоты) только для положения top
              className={cn(position === 'top' ? `index_${headerIndexBase + index}` : undefined)}
            >
              {columnKeys.map((columnKey, columnIndex) => (
                <Th
                  key={columnKey}
                  bgFilled={sticky}
                  ignoreFirstChild
                  ignoreLastChild={hasRightTable}
                  {...mergePropsWithClassName(
                    headerProps[columnKey],
                    `${sameTableColumnBaseClassName}${columnIndex + columnIndexShift}`,
                    createWidthCopyClassName(columnKey),
                  )}
                  onMouseEnter={onCellMouseEnter}
                  onMouseLeave={onCellMouseLeave}
                  verticalAlignTop={headerCellsVerticalAlignTop}
                  verticalAlignBottom={headerCellsVerticalAlignBottom}
                >
                  {renderHeaderCell({
                    columnKey,
                    value:
                      typeof header[columnKey] === 'string'
                        ? (header[columnKey] as string | undefined)
                        : undefined,
                  })}
                </Th>
              ))}
            </Tr>
          ))}
        </thead>
      )}

      {/* рендерится только в положении middle */}
      {position === 'middle' && (
        <tbody>
          {actualRows.map(({ rowKey, rowData }, rowIndex) => (
            <Tr
              key={rowKey}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              // ref для копирования высоты - всегда, когда рендерится (middle)
              reference={rowReferenceForCopyHeight}
              // index (для копирования высоты) - всегда, когда видимо (middle)
              // sameTableRowBaseClassName - для посветки наведения
              className={cn(
                rowClassName,
                `${sameTableRowBaseClassName}${rowIndex}`,
                `index_${rowIndex}`,
              )}
              loading={loading && !nothing}
              interlineBorder
            >
              {columnKeysMain.map((columnKey, columnIndex) => (
                <Td
                  key={columnKey}
                  {...cellsTdCommonProps}
                  {...mergePropsWithClassName(
                    rowsDataProps[columnKey],
                    `${sameTableColumnBaseClassName}${columnIndex + columnIndexShift}`,
                  )}
                  {...tdPropsByRowAndColumnKey({ side: 'main', rowKey, columnKey })}
                  ignoreFirstChild
                  ignoreLastChild={hasRightTable}
                >
                  {loading && nothing
                    ? renderLoadingCell(columnKey)
                    : renderCell(rowData[columnKey])}
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      )}

      {/* в положении top не рендерится
       в положении middle скрывается */}
      {total && totalProps && renderFooterCell && position !== 'top' && (
        <tfoot>
          {total.map(({ rowKey, rowData }, rowIndex) => (
            <Tr
              key={rowKey}
              hidden={position === 'middle'}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              // ref для копирования высоты - только для положения bottom (когда видимо)
              // ref для копирования ширины ячеек, middle (from) и bottom (to)
              reference={
                position === 'bottom'
                  ? rowReferenceBothForFooters
                  : position === 'middle'
                    ? rowReferenceForCopyCellWidthsForFooters
                    : undefined
              }
              // index (для копирования высоты) - только для положения bottom (когда видимо)
              className={cn(
                `${sameTableRowBaseClassName}${rowIndex + footerIndexBase}`,
                position === 'bottom' ? `index_${rowIndex + footerIndexBase}` : undefined,
              )}
              loading={loading && !nothing && position === 'bottom'}
              interlineBorder
            >
              {columnKeysMain.map((columnKey, columnIndex) => (
                <Td
                  key={columnKey}
                  bgFilled={sticky}
                  ignoreFirstChild
                  ignoreLastChild={hasRightTable}
                  {...cellsTdCommonProps}
                  {...totalProps[columnKey]}
                  {...mergePropsWithClassName(
                    totalProps[columnKey],
                    `${sameTableColumnBaseClassName}${columnIndex + columnIndexShift}`,
                    createWidthCopyClassName(columnKey),
                  )}
                >
                  {loading && nothing
                    ? renderLoadingCell(columnKey)
                    : renderFooterCell(rowData[columnKey])}
                </Td>
              ))}
            </Tr>
          ))}
        </tfoot>
      )}
    </Table>
  )
}
